/**
 * Agents Service
 *
 * This service provides access to the MCP agent layer through the unified AI gateway.
 * All AI operations are routed through /api/ai/* endpoints on the backend.
 */

import { axios } from "../libs/axios";

export type MarketDNA = {
  role_summary: string;
  top_hard_skills: string[];
  top_soft_skills: string[];
  tech_stack: string[];
  hidden_requirements: string[];
  keywords_for_ats: string[];
  salary_range: string | null;
  interview_focus: string;
  sources?: {
    onet_available: boolean;
    live_jobs_count: number;
    perplexity_insights_available: boolean;
  };
  // Raw Data Fields
  onet_data?: any;
  exa_jobs?: any[];
  perplexity_research?: string;
};

export type InterviewHistory = {
  question: string;
  answer: string;
};

export type InterrogatorResponse = {
  analysis: {
    current_gap: string;
    reasoning: string;
  };
  next_action: {
    type: "question" | "complete";
    topic?: string;
    text_to_user?: string;
    achievements?: string[];
  };
  status: "continue" | "complete";
};

export type CoverLetterResult = {
  content: string;
  jobTitle: string;
  company: string;
  tone: string;
  language: string;
  wordCount: number;
};

export type GenerateCoverLetterParams = {
  jobTitle: string;
  company: string;
  jobDescription: string;
  resumeContent: string;
  tone?: string;
  language?: string;
};

export const agentsService = {
  // Market Scout - analyzes job market for role and location
  // `bundled` skips per-call billing when part of a resume-generation flow
  // (the flat RESUME_GENERATION fee is charged once at generation).
  scoutMarket: async (
    role: string,
    location: string,
    opts?: { bundled?: boolean },
  ): Promise<MarketDNA> => {
    try {
      const response = await axios.post(
        "/ai/market/scout",
        { role, location },
        opts?.bundled ? { headers: { "X-Bundled-Billing": "1" } } : undefined,
      );
      return response.data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, "Failed to analyze market"));
    }
  },

  // Cover Letter Generation - AI-powered cover letter
  generateCoverLetter: async (params: GenerateCoverLetterParams): Promise<CoverLetterResult> => {
    const response = await axios.post("/ai/write/cover-letter", params);
    return response.data;
  },

  // Interrogator - generates interview questions
  // `bundled` skips per-question billing when part of a resume-generation flow.
  interrogate: async (
    marketDNA: MarketDNA,
    userProfile: any,
    history: InterviewHistory[],
    opts?: { bundled?: boolean },
  ): Promise<InterrogatorResponse> => {
    try {
      const response = await axios.post(
        "/ai/interview/question",
        {
          marketDNA,
          userProfile,
          history,
        },
        opts?.bundled ? { headers: { "X-Bundled-Billing": "1" } } : undefined,
      );
      return response.data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, "Failed to generate interview question"));
    }
  },

  // Architect - generates resume content
  architect: async (
    marketDNA: MarketDNA,
    verifiedEvidence: InterviewHistory[],
    targetCulture: string,
    language?: string,
  ): Promise<any> => {
    const response = await axios.post("/ai/resume/generate", {
      marketDNA,
      verifiedEvidence,
      targetCulture,
      ...(language ? { language } : {}),
    });
    return response.data;
  },

  // Formatter - formats resume content (still uses legacy endpoint)
  formatResume: async (resumeContent: any, language?: string): Promise<any> => {
    const response = await axios.post("/agents/formatter/format", {
      resumeContent,
      ...(language ? { language } : {}),
    });
    return response.data;
  },

  // Writer - generate a professional summary from a job title
  // `billAsGeneration` charges the flat RESUME_GENERATION fee instead of the
  // standalone AI_SUMMARY fee (used by the Voice Interview save flow).
  generateSummary: async (
    jobTitle: string,
    language?: string,
    opts?: { billAsGeneration?: boolean },
  ): Promise<string> => {
    const response = await axios.post(
      "/ai/write/summary",
      {
        jobTitle,
        ...(language ? { language } : {}),
      },
      opts?.billAsGeneration ? { headers: { "X-Bill-As": "resume_generation" } } : undefined,
    );
    const data = response.data;
    if (typeof data === "string") return data;
    return data?.result ?? data?.summary ?? "";
  },

  // Designer - generates design from image (still uses legacy endpoint)
  generateDesign: async (imageUrl: string): Promise<{ html: string }> => {
    const response = await axios.post("/agents/designer/generate", { imageUrl });
    return response.data;
  },

  // Resume Roast - Stylion AI sarcastic critique
  roastResume: async (resumeJson: any, language?: string): Promise<any> => {
    const response = await axios.post("/ai/resume/roast", { resumeJson, language });
    return response.data;
  },

  // ATS Score - analyzes resume against job description
  atsScore: async (resumeJson: any, jobDescription: string, marketContext?: any): Promise<any> => {
    const response = await axios.post("/ai/ats/score", {
      resumeJson,
      jobDescription,
      marketContext,
    });
    return response.data;
  },

  // Files - generates PDF (still uses legacy endpoint)
  generateFilesPdf: async (resumeJson: any): Promise<any> => {
    const response = await axios.post("/agents/files/pdf", {
      resumeJson,
    });
    return response.data;
  },

  // Refine Segment - targeted optimization of a specific resume section
  refineSegment: async (
    sectionName: string,
    sectionData: any,
    suggestion: string,
    targetRole: string,
  ): Promise<any> => {
    const response = await axios.post("/ai/resume/refine", {
      sectionName,
      sectionData,
      suggestion,
      targetRole,
    });
    return response.data;
  },

  // Designer - populates template (still uses legacy endpoint)
  populateDesign: async (templateHtml: string, userData: any): Promise<{ html: string }> => {
    const response = await axios.post("/agents/designer/populate", {
      templateHtml,
      userData,
    });
    return response.data;
  },

  // Designer - generates PDF from HTML (still uses legacy endpoint)
  generatePdf: async (html: string): Promise<Blob> => {
    const response = await axios.post("/agents/designer/pdf", { html }, { responseType: "blob" });
    return response.data;
  },

  achievements: {
    async getBadges(): Promise<string[]> {
      return []; // Mock for now
    },
  },
};

function extractApiErrorMessage(error: unknown, fallback: string): string {
  const isAxiosErr = (axios as any).isAxiosError?.(error);
  if (isAxiosErr) {
    const responseMessage = (error as any)?.response?.data?.message;
    if (Array.isArray(responseMessage) && responseMessage.length > 0) {
      return responseMessage.join(", ");
    }
    if (typeof responseMessage === "string" && responseMessage.trim().length > 0) {
      return responseMessage;
    }
    const status = (error as any)?.response?.status;
    if (typeof status === "number") {
      return `${fallback} (${status})`;
    }
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallback;
}
